import { motion } from 'framer-motion'
import Link from '../components/LocalizedLink'
import { useState, useEffect } from 'react'
import { Book, Video, HelpCircle, Mail, MessageCircle, FileText, Search, ChevronDown, ChevronUp, Download, ExternalLink, Youtube } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'

const BASE = import.meta.env.BASE_URL
const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@AutoMechLabs-WA'

const documentation = {
  el: [
    { id: 1, title: 'Οδηγός Χρήστη', description: 'Πλήρης οδηγός χρήσης του Workshop Assistant — λειτουργίες, ροές εργασίας, δημιουργία προσφορών και προχωρημένες ρυθμίσεις.', language: 'ΕΛ', flag: '🇬🇷', filename: 'Sheet_Metal_Estimator_User_Manual_EL.pdf', size: '~2 MB', category: 'Εγχειρίδιο' },
    { id: 2, title: 'Οδηγός Σύνδεσης Inventor', description: 'Πώς να ρυθμίσετε το add-in του Autodesk Inventor που εξάγει δεδομένα εξαρτημάτων απευθείας στο Workshop Assistant.', language: 'ΕΛ', flag: '🇬🇷', filename: 'SheetMetalToJSON_Installation_Guide_GR.pdf', size: '~1 MB', category: 'Οδηγός' },
    { id: 3, title: 'Οδηγός Σύνδεσης SolidWorks', description: 'Πώς να ρυθμίσετε το add-in του SolidWorks για εξαγωγή δεδομένων ελάσματος απευθείας στο Workshop Assistant.', language: 'ΕΛ', flag: '🇬🇷', filename: 'SheetMetalToJSON_SolidWorks_Installation_Guide_GR.pdf', size: '~1 MB', category: 'Οδηγός' },
  ],
  en: [
    { id: 1, title: 'User Manual', description: 'Complete guide to using Workshop Assistant — features, workflows, offer creation, and advanced settings.', language: 'EN', flag: '🇬🇧', filename: 'Sheet_Metal_Estimator_User_Manual_EN.pdf', size: '~2 MB', category: 'Manual' },
    { id: 2, title: 'Inventor Connection Guide', description: 'How to set up the Autodesk Inventor add-in that exports sheet metal part data directly into Workshop Assistant.', language: 'EN', flag: '🇬🇧', filename: 'SheetMetalToJSON_Installation_Guide_EN.pdf', size: '~1 MB', category: 'Guide' },
    { id: 3, title: 'SolidWorks Connection Guide', description: 'How to set up the SolidWorks add-in that exports sheet metal part data directly into Workshop Assistant.', language: 'EN', flag: '🇬🇧', filename: 'SheetMetalToJSON_SolidWorks_Installation_Guide_EN.pdf', size: '~1 MB', category: 'Guide' },
  ],
}

const categoryColors = {
  Manual: 'bg-blue-100 text-blue-700', Εγχειρίδιο: 'bg-blue-100 text-blue-700',
  Guide: 'bg-emerald-100 text-emerald-700', Οδηγός: 'bg-emerald-100 text-emerald-700',
}

const translations = {
  el: {
    hero: { title: 'Πώς Μπορούμε να Βοηθήσουμε;', subtitle: 'Βρείτε απαντήσεις, οδηγούς και πόρους υποστήριξης', searchPlaceholder: 'Αναζήτηση...' },
    tabs: { docs: 'Τεκμηρίωση', videos: 'Βίντεο Οδηγοί' },
    docsSection: { title: 'Τεκμηρίωση', subtitle: 'Κατεβάστε ή προβάλετε τους οδηγούς PDF απευθείας στο πρόγραμμα περιήγησης.', view: 'Προβολή', download: 'Λήψη' },
    videosSection: { title: 'Βίντεο Οδηγοί', subtitle: 'Αναλυτικά βίντεο για να αξιοποιήσετε στο έπακρο το Workshop Assistant.', channelCta: 'Το κανάλι μας στο YouTube', watch: 'Δείτε στο YouTube', loadError: 'Δεν ήταν δυνατή η φόρτωση των βίντεο. Επισκεφθείτε το κανάλι μας στο YouTube.' },
    faq: {
      title: 'Συχνές Ερωτήσεις', subtitle: 'Γρήγορες απαντήσεις σε συνηθισμένες ερωτήσεις',
      noResults: 'Δεν βρέθηκαν αποτελέσματα για', noResultsHint: 'Δοκιμάστε διαφορετικές λέξεις-κλειδιά ή επικοινωνήστε με την υποστήριξη',
      categories: [
        { category: 'Ξεκινώντας', questions: [
          { q: 'Πώς εγκαθιστώ το Workshop Assistant;', a: 'Κατεβάστε το πρόγραμμα εγκατάστασης από την ιστοσελίδα μας, εκτελέστε το και ακολουθήστε τον οδηγό εγκατάστασης. Η διαδικασία διαρκεί λιγότερο από 2 λεπτά.' },
          { q: 'Πώς ενεργοποιώ την άδεια χρήσης μου;', a: 'Μετά την εγκατάσταση, κάντε κλικ στο εικονίδιο του καλαθιού πάνω δεξιά, αντιγράψτε το ID ενεργοποίησης, στείλτε μας και εισάγετε τον σειριακό κωδικό που σας παρέχουμε.' },
          { q: 'Μπορώ να δοκιμάσω πριν αγοράσω;', a: 'Ναι! Η δωρεάν δοκιμή διαρκεί 5 ημέρες με πλήρη πρόσβαση σε λειτουργίες. Δεν απαιτείται πιστωτική κάρτα.' },
        ]},
        { category: 'Χρήση Εφαρμογής', questions: [
          { q: 'Πώς εισάγω αρχείο DXF;', a: 'Μεταβείτε στην καρτέλα Εισαγωγή Αρχείου, επιλέξτε DXF ως τύπο αρχείου, επιλέξτε το αρχείο σας, φάκελο εξόδου και υλικό, εισάγετε πάχος και κάντε κλικ Μετατροπή.' },
          { q: 'Ποια η διαφορά μεταξύ laser και ψαλιδιού;', a: 'Η κοπή laser είναι πιο ακριβής και χειρίζεται σύνθετα σχήματα. Το ψαλίδι είναι ταχύτερο και φθηνότερο για ευθείες κοπές. Η εφαρμογή επιλέγει αυτόματα βάσει γεωμετρίας.' },
          { q: 'Πώς δημιουργώ προσφορά χωρίς CAD;', a: 'Χρησιμοποιήστε την καρτέλα Χειροκίνητο Εξάρτημα. Επιλέξτε σχήμα, εισάγετε διαστάσεις, οπές και κάμψεις. Η εφαρμογή υπολογίζει όλα αυτόματα.' },
        ]},
        { category: 'Παράμετροι & Ρυθμίσεις', questions: [
          { q: 'Πώς ενημερώνω το κόστος υλικών;', a: 'Μεταβείτε στον Επεξεργαστή Παραμέτρων, στην ενότητα Κόστος Υλικών, ενημερώστε τις τιμές ανά kg και κάντε κλικ Αποθήκευση Αλλαγών.' },
          { q: 'Μπορώ να αλλάξω το περιθώριο κέρδους;', a: 'Ναι, στον Επεξεργαστή Παραμέτρων σε κάθε ενότητα μηχανήματος μπορείτε να ορίσετε προσαρμοσμένα ποσοστά κέρδους.' },
          { q: 'Πώς προσθέτω νέο υλικό;', a: 'Χρησιμοποιήστε τον Επεξεργαστή Υλικών για να προσθέσετε νέα υλικά χωρίς χειροκίνητη επεξεργασία JSON.' },
        ]},
        { category: 'Αντιμετώπιση Προβλημάτων', questions: [
          { q: 'Η εφαρμογή δεν ξεκινά. Τι να κάνω;', a: 'Δοκιμάστε εκτέλεση ως διαχειριστής. Αν δεν λειτουργεί, διαγράψτε license.json και calc_params.json από τον φάκελο εγκατάστασης και επανεκκινήστε.' },
          { q: 'Η εισαγωγή DXF αποτυγχάνει. Γιατί;', a: 'Βεβαιωθείτε ότι το αρχείο DXF είναι έγκυρο. Δοκιμάστε να το ανοίξετε στο λογισμικό CAD πρώτα. Πρέπει να είναι 2D σχέδιο με κλειστά περιγράμματα.' },
          { q: 'Τα κόστη φαίνονται λανθασμένα. Πώς επαληθεύω;', a: 'Ελέγξτε τις παραμέτρους. Επαληθεύστε ισχύ μηχανήματος, τιμές ηλεκτρισμού, εργατικά και κόστη υλικών για την περιοχή σας.' },
        ]},
      ],
    },
    contact: {
      title: 'Χρειάζεστε Ακόμα Βοήθεια;', subtitle: 'Η ομάδα υποστήριξής μας είναι εδώ για να σας βοηθήσει',
      email: { title: 'Υποστήριξη Email', desc: 'Συνήθως απαντάμε εντός 24 ωρών', cta: 'Στείλτε Email →' },
      phone: { title: 'Τηλεφωνική Υποστήριξη', desc: 'Δευ-Παρ, 9π.μ.-5μ.μ. (EET)', cta: '+30 695 195 6643 →' },
      form: { title: 'Φόρμα Επικοινωνίας', desc: 'Στείλτε μας λεπτομερές μήνυμα', cta: 'Συμπλήρωση →' },
    },
  },
  en: {
    hero: { title: 'How Can We Help?', subtitle: 'Find answers, guides, and support resources', searchPlaceholder: 'Search for help...' },
    tabs: { docs: 'Documentation', videos: 'Video Tutorials' },
    docsSection: { title: 'Documentation', subtitle: 'Download or view PDF guides directly in your browser.', view: 'View', download: 'Download' },
    videosSection: { title: 'Video Tutorials', subtitle: 'Step-by-step video walkthroughs to help you master Workshop Assistant.', channelCta: 'Visit our YouTube channel', watch: 'Watch on YouTube', loadError: 'Videos could not be loaded right now. Please visit our YouTube channel.' },
    faq: {
      title: 'Frequently Asked Questions', subtitle: 'Quick answers to common questions',
      noResults: 'No results found for', noResultsHint: 'Try different keywords or contact support',
      categories: [
        { category: 'Getting Started', questions: [
          { q: 'How do I install Workshop Assistant?', a: 'Download the installer from our website, run it, and follow the installation wizard. The process takes less than 2 minutes.' },
          { q: 'How do I activate my license?', a: "After installing, click the shopping cart icon in the top-right corner, copy your Activation ID, send it to us, and enter the Serial Key we provide." },
          { q: 'Can I try before buying?', a: 'Yes! The free trial lasts 5 days with full feature access. No credit card required.' },
        ]},
        { category: 'Using the Application', questions: [
          { q: 'How do I import a DXF file?', a: 'Go to the Import File tab, select DXF as file type, browse for your file, select output folder and material, enter thickness, and click Convert.' },
          { q: "What's the difference between laser and guillotine?", a: 'Laser cutting is more precise and handles complex shapes. Guillotine is faster and cheaper for straight cuts. The app auto-selects based on part geometry.' },
          { q: 'How do I create a quote manually without CAD?', a: 'Use the Manual Part tab. Select a shape, enter dimensions, holes, and bends. The app calculates everything automatically.' },
        ]},
        { category: 'Parameters & Configuration', questions: [
          { q: 'How do I update material costs?', a: 'Go to Parameters Editor tab, scroll to Material Costs section, update the cost per kg values, and click Save Changes.' },
          { q: 'Can I change profit markup?', a: 'Yes, in Parameters Editor under each machine section you can set custom profit markup percentages.' },
          { q: 'How do I add a new material?', a: 'Use the Materials Editor to add new materials with full configuration — no JSON editing required.' },
        ]},
        { category: 'Troubleshooting', questions: [
          { q: "The app won't start. What should I do?", a: "Try running as administrator. If that doesn't work, delete license.json and calc_params.json from the installation folder and restart." },
          { q: 'DXF import is failing. Why?', a: 'Ensure the DXF file is valid and not corrupted. Try opening it in CAD software first. The file should be a 2D drawing with closed contours.' },
          { q: 'Costs seem incorrect. How can I verify?', a: 'Check your parameters in Parameters Editor. Verify machine power, electricity rates, workforce rates, and material costs are correct for your region.' },
        ]},
      ],
    },
    contact: {
      title: 'Still Need Help?', subtitle: 'Our support team is here to assist you',
      email: { title: 'Email Support', desc: 'We typically respond within 24 hours', cta: 'Send Email →' },
      phone: { title: 'Phone Support', desc: 'Mon-Fri, 9AM-5PM (EET)', cta: '+30 695 195 6643 →' },
      form: { title: 'Contact Form', desc: 'Send us a detailed message', cta: 'Fill Form →' },
    },
  },
}

function DocCard({ doc, t }) {
  const url = `${BASE}Documentation/${doc.filename}`
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col gap-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-shrink-0 w-12 h-12 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center">
          <FileText className="h-6 w-6" />
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${categoryColors[doc.category] || 'bg-gray-100 text-gray-600'}`}>{doc.category}</span>
          <span className="text-sm font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded-full">{doc.flag} {doc.language}</span>
        </div>
      </div>
      <div className="flex-grow">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{doc.title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed">{doc.description}</p>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <span className="text-xs text-gray-400">PDF · {doc.size}</span>
        <div className="flex gap-2">
          <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 px-3 py-1.5 rounded-lg hover:bg-primary-50 transition-colors">
            <ExternalLink className="h-4 w-4" />{t.docsSection.view}
          </a>
          <a href={url} download={doc.filename} className="flex items-center gap-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 px-3 py-1.5 rounded-lg transition-colors">
            <Download className="h-4 w-4" />{t.docsSection.download}
          </a>
        </div>
      </div>
    </div>
  )
}

const Support = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedFaq, setExpandedFaq] = useState(null)
  const [activeTab, setActiveTab] = useState('documentation')
  const { lang } = useLanguage()
  const t = translations[lang]
  const docs = documentation[lang]
  const [videos, setVideos] = useState(null)
  const [videosError, setVideosError] = useState(false)

  useEffect(() => {
    if (activeTab !== 'videos' || videos) return
    fetch(`${BASE}api/youtube/videos`)
      .then(r => { if (!r.ok) throw new Error(); return r.json() })
      .then(data => setVideos(data.videos))
      .catch(() => setVideosError(true))
  }, [activeTab, videos])

  const tabs = [
    { id: 'documentation', label: t.tabs.docs, icon: Book, count: docs.length },
    { id: 'videos', label: t.tabs.videos, icon: Video },
  ]

  const filteredFaqs = t.faq.categories
    .map(category => ({ ...category, questions: category.questions.filter(q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) || q.a.toLowerCase().includes(searchQuery.toLowerCase())) }))
    .filter(category => category.questions.length > 0)

  return (
    <div className="overflow-hidden">
      <SEO
        title="Support & Documentation"
        description="User manuals, CAD plugin installation guides, and FAQs for Workshop Assistant. Get help with setup, DXF import, and CNC nesting."
        path="/support"
      />
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90 mb-8">{t.hero.subtitle}</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input type="text" placeholder={t.hero.searchPlaceholder} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-1">
              {tabs.map((tab) => {
                const Icon = tab.icon
                const isActive = activeTab === tab.id
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-6 py-4 font-medium text-sm border-b-2 transition-colors duration-200 ${isActive ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'}`}>
                    <Icon className="h-4 w-4" />{tab.label}
                    {tab.count != null && <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${isActive ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'}`}>{tab.count}</span>}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        <div className="section-container">
          <div className="max-w-5xl mx-auto">
            {activeTab === 'documentation' && (
              <div>
                <div className="mb-8"><h2 className="text-2xl font-bold text-gray-900 mb-1">{t.docsSection.title}</h2><p className="text-gray-500">{t.docsSection.subtitle}</p></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {docs.map((doc) => <DocCard key={doc.id} doc={doc} t={t} />)}
                </div>
              </div>
            )}
            {activeTab === 'videos' && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                  <div><h2 className="text-2xl font-bold text-gray-900 mb-1">{t.videosSection.title}</h2><p className="text-gray-500">{t.videosSection.subtitle}</p></div>
                  <a href={YOUTUBE_CHANNEL_URL} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 self-start md:self-auto bg-red-600 hover:bg-red-700 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors duration-200">
                    <Youtube className="h-4 w-4" />{t.videosSection.channelCta}<ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </div>
                {videosError ? (
                  <p className="text-gray-500 py-12 text-center">{t.videosSection.loadError}</p>
                ) : !videos ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[0, 1, 2].map(i => <div key={i} className="rounded-xl bg-gray-200 animate-pulse aspect-video" />)}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {videos.map(video => (
                      <a key={video.id} href={`${BASE}api/youtube/go/${video.id}`} target="_blank" rel="noopener noreferrer"
                        className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-200 flex flex-col">
                        <div className="relative aspect-video bg-gray-900 overflow-hidden">
                          <img src={video.thumbnail} alt={video.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors duration-200">
                            <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
                              <svg viewBox="0 0 24 24" className="h-6 w-6 text-white ml-1" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 flex flex-col flex-1">
                          <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-200 mb-2">{video.title}</h3>
                          <div className="mt-auto flex items-center justify-between text-sm text-gray-500">
                            <span>{video.published && new Date(video.published).toLocaleDateString(lang === 'el' ? 'el-GR' : 'en-GB')}</span>
                            <span className="inline-flex items-center gap-1 text-red-600 font-medium">{t.videosSection.watch}<ExternalLink className="h-3.5 w-3.5" /></span>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <section id="faq" className="section-container bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">{t.faq.title}</h2>
          <p className="text-xl text-gray-600 text-center mb-12">{t.faq.subtitle}</p>
          <div className="space-y-8">
            {filteredFaqs.map((category, catIndex) => (
              <div key={catIndex}>
                <h3 className="text-2xl font-semibold mb-4 text-gray-900">{category.category}</h3>
                <div className="space-y-3">
                  {category.questions.map((faq, faqIndex) => {
                    const key = `${catIndex}-${faqIndex}`
                    const isExpanded = expandedFaq === key
                    return (
                      <motion.div key={key} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="bg-gray-50 rounded-lg overflow-hidden">
                        <button onClick={() => setExpandedFaq(isExpanded ? null : key)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors">
                          <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                          {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />}
                        </button>
                        {isExpanded && <div className="px-6 pb-4 text-gray-600">{faq.a}</div>}
                      </motion.div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          {filteredFaqs.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">{t.faq.noResults} "{searchQuery}"</p>
              <p className="text-sm text-gray-400 mt-2">{t.faq.noResultsHint}</p>
            </div>
          )}
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">{t.contact.title}</h2>
          <p className="text-xl text-gray-600 mb-8">{t.contact.subtitle}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a href="mailto:contact@automechlabs.com" className="card hover:border-primary-500 border-2 border-transparent group">
              <Mail className="h-12 w-12 text-primary-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold mb-2">{t.contact.email.title}</h3>
              <p className="text-sm text-gray-600 mb-3">{t.contact.email.desc}</p>
              <span className="text-primary-600 font-medium group-hover:underline">{t.contact.email.cta}</span>
            </a>
            <a href="tel:+306951956643" className="card hover:border-primary-500 border-2 border-transparent group">
              <MessageCircle className="h-12 w-12 text-primary-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold mb-2">{t.contact.phone.title}</h3>
              <p className="text-sm text-gray-600 mb-3">{t.contact.phone.desc}</p>
              <span className="text-primary-600 font-medium group-hover:underline">{t.contact.phone.cta}</span>
            </a>
            <Link to="/contact" className="card hover:border-primary-500 border-2 border-transparent group">
              <FileText className="h-12 w-12 text-primary-600 mx-auto mb-4 group-hover:scale-110 transition-transform" />
              <h3 className="font-semibold mb-2">{t.contact.form.title}</h3>
              <p className="text-sm text-gray-600 mb-3">{t.contact.form.desc}</p>
              <span className="text-primary-600 font-medium group-hover:underline">{t.contact.form.cta}</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Support
